import i18n from "../../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { farmCalendarParts, farmDaysInMonth } from "@/lib/farmTime";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PayrollMetricCard, PayrollSummaryField, PayrollTd, PayrollTh, hoursLabel } from "@/components/payroll/PayrollUi";
import { toast } from "sonner";
import { CalendarDays, Clock3, Printer, ReceiptText, Wallet } from "lucide-react";

export default function EmployeePayrollPage() {
    const farmNow = farmCalendarParts();
    const [tab, setTab] = useState("timesheet");
    const [year, setYear] = useState(farmNow.year);
    const [month, setMonth] = useState(farmNow.month);
    const [timesheetRows, setTimesheetRows] = useState([]);
    const [payslips, setPayslips] = useState([]);
    const [summary, setSummary] = useState(null);
    const [calendar, setCalendar] = useState(null);
    const [eligibleAttendanceRows, setEligibleAttendanceRows] = useState([]);
    const [selectedAttendanceId, setSelectedAttendanceId] = useState("");
    const [overtimeReason, setOvertimeReason] = useState("");
    const [requestedHours, setRequestedHours] = useState("");
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailRow, setDetailRow] = useState(null);

    const loadTimesheet = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/timesheets", { params: { year, month, period: "monthly" } });
            setTimesheetRows(Array.isArray(data?.rows) ? data.rows : []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load timesheet");
            setTimesheetRows([]);
        }
    }, [year, month]);

    const loadPayslips = useCallback(async () => {
        try {
            const { data } = await api.get("/payroll/my-payslips", { params: { year, month } });
            setPayslips(Array.isArray(data?.rows) ? data.rows : []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load payslips");
            setPayslips([]);
        }
    }, [year, month]);

    const loadSummary = useCallback(async () => {
        try {
            const [{ data: pay }, { data: cal }] = await Promise.all([
                api.get("/payroll/my-summary", { params: { year, month } }),
                api.get("/attendance/my-summary", { params: { year, month } }),
            ]);
            setSummary(pay?.summary || null);
            setCalendar(cal || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load payroll summary");
            setSummary(null);
            setCalendar(null);
        }
    }, [year, month]);

    const loadEligibleAttendanceRows = useCallback(async () => {
        const start = `${year}-${String(month).padStart(2, "0")}-01`;
        const end = `${year}-${String(month).padStart(2, "0")}-${String(farmDaysInMonth(year, month)).padStart(2, "0")}`;
        try {
            const { data } = await api.get("/attendance/logs", { params: { from: start, to: end } });
            const rows = (Array.isArray(data?.rows) ? data.rows : []).filter((row) => row?.is_finalized && row?.id);
            setEligibleAttendanceRows(rows);
            setSelectedAttendanceId((current) => {
                if (current && rows.some((row) => row.id === current)) return current;
                return rows[0]?.id || "";
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load completed attendance records");
            setEligibleAttendanceRows([]);
            setSelectedAttendanceId("");
        }
    }, [year, month]);

    useEffect(() => {
        loadTimesheet();
        loadPayslips();
        loadSummary();
        loadEligibleAttendanceRows();
    }, [loadTimesheet, loadPayslips, loadSummary, loadEligibleAttendanceRows]);

    const currentTimesheet = useMemo(() => (timesheetRows || [])[0] || null, [timesheetRows]);

    const requestOvertime = async () => {
        if (!selectedAttendanceId) {
            toast.error("Select a completed attendance record ID first");
            return;
        }
        if (!overtimeReason.trim()) {
            toast.error("Overtime reason is required");
            return;
        }
        const hours = Number(requestedHours || 0);
        if (!Number.isFinite(hours) || hours <= 0) {
            toast.error("Requested hours must be greater than zero");
            return;
        }
        try {
            await api.post("/attendance/overtime", {
                attendance_id: selectedAttendanceId,
                requested_overtime_hours: hours,
                reason: overtimeReason.trim(),
            });
            toast.success("Overtime request submitted");
            setOvertimeReason("");
            setRequestedHours("");
            await loadSummary();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit overtime request");
        }
    };

    const openPayslip = (row) => {
        setDetailRow(row);
        setDetailsOpen(true);
    };

    const printPayslip = (row) => {
        try {
            const jsPDF = require("jspdf").default;
            const doc = new jsPDF();
            doc.setFontSize(18);
            doc.text("Employee Payslip", 14, 20);
            doc.setFontSize(11);
            let y = 32;
            const lines = [
                ["Period", row.period || row.created_at?.slice(0, 7) || "-"],
                ["Gross Pay", formatCurrency(row.gross_pay || 0)],
                ["Total Deductions", formatCurrency(row.total_deductions || 0)],
                ["Net Pay", formatCurrency(row.net_pay || 0)],
                ["SSS", formatCurrency(row.sss_employee || 0)],
                ["PhilHealth", formatCurrency(row.philhealth_employee || 0)],
                ["Pag-IBIG", formatCurrency(row.pagibig_employee || 0)],
                ["Withholding Tax", formatCurrency(row.withholding_tax || 0)],
            ];
            lines.forEach(([label, value]) => {
                doc.text(String(label), 14, y);
                doc.text(String(value), 180, y, { align: "right" });
                y += 7;
            });
            doc.save(`employee-payslip-${(row.period || row.id || "payslip").replace(/[^a-z0-9_-]+/gi, "_")}.pdf`);
        } catch (_err) {
            toast.error("Failed to generate payslip PDF");
        }
    };

    return (
        <div className="space-y-4" data-testid="employee-payroll-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{i18n.t("batch1.d45987441aa0")}</div>
                <h1 className="text-2xl font-bold mt-1">{i18n.t("batch1.b4ceeaaca95c")}</h1>
                <p className="text-sm text-muted-foreground">{i18n.t("batch1.751d98434e7f")}</p>
            </div>

            <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.879e32326c52")}</Label>
                        <Input type="number" className="mt-2 h-10" value={year} onChange={(e) => setYear(Number(e.target.value || farmNow.year))} />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.082bc378cd60")}</Label>
                        <Input type="number" min="1" max="12" className="mt-2 h-10" value={month} onChange={(e) => setMonth(Number(e.target.value || 1))} />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                        <Button className="h-10" onClick={() => { loadTimesheet(); loadPayslips(); loadSummary(); }}>{i18n.t("batch1.56e3badc4e6c")}</Button>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <PayrollMetricCard icon={Clock3} label={i18n.t("batch1.6beef1dfb098")} value={hoursLabel(summary?.regular_hours || currentTimesheet?.regular_hours || 0)} />
                <PayrollMetricCard icon={Clock3} label={i18n.t("batch1.05b1368a3a3a")} value={hoursLabel(summary?.approved_overtime_hours || 0)} />
                <PayrollMetricCard icon={Wallet} label={i18n.t("batch1.cafacb251b01")} value={summary?.net_pay == null ? "-" : formatCurrency(summary.net_pay, summary.currency || "PHP")} />
                <PayrollMetricCard icon={CalendarDays} label={i18n.t("batch1.c4551e54f944")} value={calendar?.summary?.present_days || 0} />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap">
                    <TabsTrigger value="timesheet">{i18n.t("batch1.5122ab8cc651")}</TabsTrigger>
                    <TabsTrigger value="overtime">{i18n.t("batch1.fdb013e14248")}</TabsTrigger>
                    <TabsTrigger value="payslips">{i18n.t("batch1.93111a84e1f4")}</TabsTrigger>
                    <TabsTrigger value="summary">{i18n.t("batch1.8dcb2a02f748")}</TabsTrigger>
                </TabsList>

                <TabsContent value="timesheet" className="space-y-4">
                    <Card className="p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr><PayrollTh>{i18n.t("batch1.4e9f7a31eef4")}</PayrollTh><PayrollTh>{i18n.t("batch1.4310ed540c1c")}</PayrollTh><PayrollTh>{i18n.t("batch1.e92452c8d3e2")}</PayrollTh><PayrollTh>{i18n.t("batch1.777800dce2f6")}</PayrollTh><PayrollTh>{i18n.t("batch1.05b1368a3a3a")}</PayrollTh><PayrollTh>{i18n.t("batch1.6881cf0ce1e8")}</PayrollTh><PayrollTh>{i18n.t("batch1.9f20399258fc")}</PayrollTh></tr>
                            </thead>
                            <tbody>
                                {currentTimesheet ? (
                                    <tr className="border-t border-border/50">
                                        <PayrollTd>{currentTimesheet.days_present || 0}</PayrollTd>
                                        <PayrollTd>{currentTimesheet.days_late || 0}</PayrollTd>
                                        <PayrollTd>{currentTimesheet.days_absent || 0}</PayrollTd>
                                        <PayrollTd>{hoursLabel(currentTimesheet.regular_hours)}</PayrollTd>
                                        <PayrollTd>{hoursLabel(currentTimesheet.overtime_hours)}</PayrollTd>
                                        <PayrollTd>{hoursLabel(currentTimesheet.pending_overtime_hours)}</PayrollTd>
                                        <PayrollTd>{currentTimesheet.estimated_gross_pay == null ? "-" : formatCurrency(currentTimesheet.estimated_gross_pay, calendar?.settings?.payroll_currency || "PHP")}</PayrollTd>
                                    </tr>
                                ) : <tr><PayrollTd colSpan={7} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.acf52fa41a9f")}</PayrollTd></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="overtime" className="space-y-4">
                    <Card className="p-4 space-y-3">
                        <div>
                            <Label className="tiny-label">{i18n.t("batch1.5e04968b4f22")}</Label>
                            <Select value={selectedAttendanceId || "__none__"} onValueChange={(value) => setSelectedAttendanceId(value === "__none__" ? "" : value)}>
                                <SelectTrigger className="mt-2 h-10">
                                    <SelectValue placeholder={i18n.t("batch1.03781ceef83d")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">{i18n.t("batch1.b624f2b953b2")}</SelectItem>
                                    {eligibleAttendanceRows.map((row) => (
                                        <SelectItem key={row.id} value={row.id}>
                                            {`${row.date} · ${hoursLabel(row.regular_hours)} regular · ${hoursLabel(row.overtime_hours)} OT`}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedAttendanceId ? (
                                <div className="mt-2 text-xs text-muted-foreground break-all">{i18n.t("batch1.2d350acabd06")} {selectedAttendanceId}</div>
                            ) : null}
                        </div>
                        <div>
                            <Label className="tiny-label">{i18n.t("batch1.fb9c60d73c33")}</Label>
                            <Input type="number" className="mt-2 h-10" value={requestedHours} onChange={(e) => setRequestedHours(e.target.value)} placeholder={i18n.t("batch1.e80a55ae1d1c")} />
                        </div>
                        <div>
                            <Label className="tiny-label">{i18n.t("batch1.f219cc0614ae")}</Label>
                            <Input className="mt-2 h-10" value={overtimeReason} onChange={(e) => setOvertimeReason(e.target.value)} placeholder={i18n.t("batch1.8f7743c484f7")} />
                        </div>
                        <Button className="h-10" onClick={requestOvertime}>{i18n.t("batch1.c531413f369b")}</Button>
                        {eligibleAttendanceRows.length === 0 ? (
                            <div className="text-xs text-muted-foreground">{i18n.t("batch1.725998e1c936")}</div>
                        ) : null}
                    </Card>
                </TabsContent>

                <TabsContent value="payslips" className="space-y-4">
                    <Card className="p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr><PayrollTh>{i18n.t("batch1.170a28a9db6d")}</PayrollTh><PayrollTh>{i18n.t("batch1.9580a6176fdb")}</PayrollTh><PayrollTh>{i18n.t("batch1.757bbae2b303")}</PayrollTh><PayrollTh>{i18n.t("batch1.9bb81c2eccbe")}</PayrollTh><PayrollTh>{i18n.t("batch1.97c89a4d6630")}</PayrollTh></tr>
                            </thead>
                            <tbody>
                                {(payslips || []).map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <PayrollTd>{row.period || row.created_at?.slice(0, 7) || "-"}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.gross_pay || 0)}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.total_deductions || 0)}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.net_pay || 0)}</PayrollTd>
                                        <PayrollTd className="space-x-2">
                                            <Button size="sm" variant="outline" onClick={() => openPayslip(row)}>{i18n.t("batch1.69bd4ef9fbd0")}</Button>
                                            <Button size="sm" variant="ghost" onClick={() => printPayslip(row)} aria-label={i18n.t("batch1.f39e8bce1e61")}><Printer className="w-4 h-4" /></Button>
                                        </PayrollTd>
                                    </tr>
                                ))}
                                {payslips.length === 0 && <tr><PayrollTd colSpan={5} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.03e2ba65ef8e")}</PayrollTd></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="summary" className="space-y-4">
                    <Card className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <PayrollSummaryField label={i18n.t("batch1.29353c2b2528")} value={summary?.monthly_salary == null ? "-" : formatCurrency(summary.monthly_salary, summary.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.fe3d54900d91")} value={summary?.daily_rate == null ? "-" : formatCurrency(summary.daily_rate, summary.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.26ede189a778")} value={summary?.hourly_rate == null ? "-" : formatCurrency(summary.hourly_rate, summary.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.6beef1dfb098")} value={hoursLabel(summary?.regular_hours || 0)} />
                        <PayrollSummaryField label={i18n.t("batch1.05b1368a3a3a")} value={hoursLabel(summary?.approved_overtime_hours || 0)} />
                        <PayrollSummaryField label={i18n.t("batch1.6881cf0ce1e8")} value={hoursLabel(summary?.pending_overtime_hours || 0)} />
                        <PayrollSummaryField label={i18n.t("batch1.cf25e1aabf83")} value={formatCurrency(summary?.late_deduction || 0, summary?.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.90b257791180")} value={formatCurrency(summary?.absent_deduction || 0, summary?.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.60a83b2a7fc8")} value={formatCurrency(summary?.allowances || 0, summary?.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.e144f64b4481")} value={formatCurrency(summary?.bonuses || 0, summary?.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.f5dec71ed241")} value={formatCurrency(summary?.other_deductions || 0, summary?.currency || "PHP")} />
                        <PayrollSummaryField label={i18n.t("batch1.cafacb251b01")} value={summary?.net_pay == null ? "-" : formatCurrency(summary.net_pay, summary.currency || "PHP")} />
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch1.71c29c587ed3")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.a16920d0c9e8")}</DialogDescription>
                    </DialogHeader>
                    {detailRow ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <PayrollSummaryField label={i18n.t("batch1.b2ea2a0a4e72")} value={formatCurrency(detailRow.gross_pay || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.a75db0499b89")} value={formatCurrency(detailRow.net_pay || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.538b62aa5eee")} value={formatCurrency(detailRow.total_deductions || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.f5dc67c95488")} value={formatCurrency(detailRow.sss_employee || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.be3d534838ef")} value={formatCurrency(detailRow.philhealth_employee || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.e148523dfcef")} value={formatCurrency(detailRow.pagibig_employee || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.da4a2c39af98")} value={formatCurrency(detailRow.withholding_tax || 0)} />
                                <PayrollSummaryField label={i18n.t("batch1.3174c06b9dad")} value={formatCurrency(detailRow.thirteenth_month_accrual || 0)} />
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
